import { useState, useEffect, useRef, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Search, User, Phone, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import {
  fetchMemberAccessibleIntegrationIds,
  WHATSAPP_INTEGRATION_PROVIDERS,
} from "@/lib/member-accessible-integrations";
import {
  dedupeSearchPreviewByPhone,
  prepareLeadSearchTerm,
  sortLeadsBySearchRelevance,
} from "@/lib/leads-kanban-utils";
import { whatsappIntegrationDisplayLabel } from "@/lib/whatsapp-integration-label";
import { LeadTagBadge } from "@/components/leads/LeadTagBadge";
import type { LeadTag } from "@/types/lead";

interface Lead {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  created_at?: string;
  tags?: LeadTag[];
  metadata?: {
    profile_picture?: string;
    instagram_id?: string;
    instagram_username?: string;
  };
}

async function fetchTagsByLeadIds(leadIds: string[]): Promise<Map<string, LeadTag[]>> {
  const tagsByLead = new Map<string, LeadTag[]>();
  if (leadIds.length === 0) return tagsByLead;

  const chunkSize = 500;
  for (let i = 0; i < leadIds.length; i += chunkSize) {
    const chunk = leadIds.slice(i, i + chunkSize);
    const { data, error } = await supabase
      .from("lead_tag_assignments")
      .select("lead_id, tag:tag_id (id, name, color)")
      .in("lead_id", chunk);

    if (error) throw error;

    for (const row of (data as { lead_id: string; tag: LeadTag | null }[]) || []) {
      if (!row.lead_id || !row.tag) continue;
      if (!tagsByLead.has(row.lead_id)) tagsByLead.set(row.lead_id, []);
      tagsByLead.get(row.lead_id)!.push(row.tag);
    }
  }

  return tagsByLead;
}

function attachTagsToLeads(leads: Lead[], tagsByLead: Map<string, LeadTag[]>): Lead[] {
  return leads.map((lead) => ({
    ...lead,
    tags: tagsByLead.get(lead.id) ?? [],
  }));
}

interface IntegrationOption {
  id: string;
  display_name: string;
}

interface SelectedConversation {
  conversation_id: string;
  channel: string;
  lead_id: string;
  lead_name: string;
  lead_phone: string | null;
  state: string;
  followup_enabled: boolean;
  metadata?: any;
}

interface NewConversationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  channel: 'whatsapp' | 'instagram';
  integrationId?: string | null;
  onConversationCreated: (conversation: SelectedConversation) => void;
}

export function NewConversationDialog({
  open,
  onOpenChange,
  workspaceId,
  channel,
  integrationId,
  onConversationCreated,
}: NewConversationDialogProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [integrations, setIntegrations] = useState<IntegrationOption[]>([]);
  const [selectedIntegrationId, setSelectedIntegrationId] = useState<string | null>(integrationId ?? null);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { toast } = useToast();
  const { user } = useAuth();

  const SEARCH_LIMIT = 50;
  const SEARCH_DEBOUNCE_MS = 300;

  const filterLeadsForChannel = useCallback(
    (rows: Lead[]) => {
      if (channel === "whatsapp") return rows.filter((lead) => lead.phone);
      return rows;
    },
    [channel],
  );

  const fetchLeads = useCallback(
    async (term: string) => {
      if (!workspaceId) return;
      setLoading(true);
      try {
        const trimmed = term.trim();
        if (trimmed) {
          const searchQuery = prepareLeadSearchTerm(trimmed);
          if (!searchQuery) {
            setLeads([]);
            return;
          }

          const { data, error } = await supabase.rpc("search_leads_by_term", {
            p_workspace_id: workspaceId,
            p_term: searchQuery,
            p_limit: SEARCH_LIMIT,
            p_funnel_id: null,
            p_include_null_funnel: false,
          });

          if (error) throw error;
          const results = sortLeadsBySearchRelevance(
            dedupeSearchPreviewByPhone((data as any[]) || []),
            trimmed,
          );
          const filtered = filterLeadsForChannel(results as unknown as Lead[]);
          const tagsByLead = await fetchTagsByLeadIds(filtered.map((lead) => lead.id));
          setLeads(attachTagsToLeads(filtered, tagsByLead));
          return;
        }

        let query = supabase
          .from("leads")
          .select("id, name, phone, email, metadata, created_at")
          .eq("workspace_id", workspaceId)
          .order("created_at", { ascending: false })
          .limit(SEARCH_LIMIT);

        if (channel === "whatsapp") {
          query = query.not("phone", "is", null);
        }

        const { data, error } = await query;
        if (error) throw error;
        const rows = (data as Lead[]) || [];
        const tagsByLead = await fetchTagsByLeadIds(rows.map((lead) => lead.id));
        setLeads(attachTagsToLeads(rows, tagsByLead));
      } catch (error) {
        console.error("Error fetching leads:", error);
        toast({
          title: "Erro ao carregar contatos",
          description: "Não foi possível carregar a lista de leads.",
          variant: "destructive",
        });
        setLeads([]);
      } finally {
        setLoading(false);
      }
    },
    [workspaceId, channel, filterLeadsForChannel, toast],
  );

  useEffect(() => {
    if (!open) {
      setSearchTerm("");
      return;
    }
    if (!workspaceId) return;

    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    const delay = searchTerm.trim().length === 0 ? 0 : SEARCH_DEBOUNCE_MS;
    searchDebounceRef.current = setTimeout(() => {
      fetchLeads(searchTerm);
    }, delay);

    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [open, workspaceId, searchTerm, fetchLeads]);

  // Buscar conexões ativas do canal (respeitando acesso do membro)
  useEffect(() => {
    if (!open || !workspaceId || !user?.id) return;

    const fetchIntegrations = async () => {
      const whatsappProviders = [...WHATSAPP_INTEGRATION_PROVIDERS];
      const [{ data, error }, accessibleIds] = await Promise.all([
        supabase
          .from("integrations")
          .select("id, provider, config, instance_identifier")
          .eq("workspace_id", workspaceId)
          .in("provider", channel === "whatsapp" ? whatsappProviders : [channel])
          .eq("status", "active")
          .order("created_at", { ascending: true }),
        fetchMemberAccessibleIntegrationIds(workspaceId, user.id),
      ]);

      if (error) {
        console.error("Error fetching integrations:", error);
        return;
      }

      const opts: IntegrationOption[] = ((data || []) as any[])
        .filter((integration) => accessibleIds.has(integration.id))
        .map((i: any) => ({
          id: i.id,
          display_name:
            channel === "whatsapp"
              ? whatsappIntegrationDisplayLabel(i)
              : i.config?.display_name || i.instance_identifier || "Conexão",
        }));
      setIntegrations(opts);

      // Inicializa seleção: prop > única disponível > nenhuma
      if (integrationId && opts.some((o) => o.id === integrationId)) {
        setSelectedIntegrationId(integrationId);
      } else if (opts.length === 1) {
        setSelectedIntegrationId(opts[0].id);
      } else if (opts.length > 0 && !selectedIntegrationId) {
        setSelectedIntegrationId(opts[0].id);
      }
    };

    fetchIntegrations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, workspaceId, channel, integrationId, user?.id]);

  // Iniciar conversa com lead selecionado
  const handleSelectLead = async (lead: Lead) => {
    if (channel === 'whatsapp' && !lead.phone) {
      toast({
        title: "Lead sem telefone",
        description: "Este lead não possui telefone cadastrado.",
        variant: "destructive",
      });
      return;
    }

    if (integrations.length > 1 && !selectedIntegrationId) {
      toast({
        title: "Selecione a conexão",
        description: "Escolha por qual número deseja iniciar a conversa.",
        variant: "destructive",
      });
      return;
    }

    setCreating(true);
    try {
      const { data, error } = await supabase.functions.invoke("start-conversation", {
        body: {
          workspaceId,
          leadId: lead.id,
          channel,
          integrationId: selectedIntegrationId,
        },
      });

      if (error) {
        console.error("start-conversation invoke error:", error);
        throw new Error(typeof error === "object" && "message" in error ? (error as any).message : String(error));
      }

      if (data?.needsIntegrationChoice) {
        // Defesa em profundidade — backend pediu escolha
        setIntegrations(data.integrations || []);
        toast({
          title: "Selecione a conexão",
          description: "Escolha por qual número deseja iniciar a conversa.",
        });
        return;
      }

      if (data?.error) {
        console.error("start-conversation data error:", data.error);
        throw new Error(data.error);
      }

      if (data?.conversation) {
        onConversationCreated({
          conversation_id: data.conversation.id,
          channel: data.conversation.channel,
          lead_id: lead.id,
          lead_name: lead.name,
          lead_phone: lead.phone,
          state: data.conversation.state || "active",
          followup_enabled: data.conversation.followup_enabled ?? true,
          metadata: lead.metadata,
        });
        onOpenChange(false);
        toast({
          title: data.isNew ? "Nova conversa criada" : "Conversa existente",
          description: data.isNew
            ? `Conversa iniciada com ${lead.name}`
            : `Abrindo conversa com ${lead.name}`,
        });
      }
    } catch (error) {
      console.error("Error starting conversation:", error);
      toast({
        title: "Erro ao iniciar conversa",
        description: error instanceof Error ? error.message : "Não foi possível criar a conversa.",
        variant: "destructive",
      });
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Nova Conversa</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 overflow-hidden">
          {/* Seletor de conexão (só aparece com 2+ conexões) */}
          {integrations.length > 1 && (
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Enviar pela conexão</Label>
              <Select
                value={selectedIntegrationId ?? undefined}
                onValueChange={(v) => setSelectedIntegrationId(v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a conexão" />
                </SelectTrigger>
                <SelectContent>
                  {integrations.map((i) => (
                    <SelectItem key={i.id} value={i.id}>
                      {i.display_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Campo de busca */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Buscar lead por nome ou telefone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
              autoFocus
            />
          </div>

          {/* Lista de leads */}
          <ScrollArea className="h-[300px] -mx-2 px-2">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : leads.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground">
                <User className="w-12 h-12 mb-2 opacity-50" />
                <p className="text-sm">
                  {searchTerm
                    ? "Nenhum lead encontrado"
                    : channel === 'whatsapp'
                    ? "Nenhum lead com telefone cadastrado"
                    : "Nenhum lead disponível"}
                </p>
              </div>
            ) : (
              <div className="space-y-1">
                {leads.map((lead) => (
                  <Button
                    key={lead.id}
                    variant="ghost"
                    className="w-full justify-start h-auto py-3 px-3"
                    onClick={() => handleSelectLead(lead)}
                    disabled={creating}
                  >
                    <Avatar className="w-10 h-10 mr-3">
                      <AvatarImage src={lead.metadata?.profile_picture} referrerPolicy="no-referrer" />
                      <AvatarFallback className="bg-primary/10 text-primary">
                        {lead.name?.charAt(0)?.toUpperCase() || "?"}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex min-w-0 flex-1 flex-col items-start text-left">
                      <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                        <span className="font-medium truncate">{lead.name}</span>
                        {(lead.tags?.length ?? 0) > 0 && (
                          <div className="flex flex-wrap items-center gap-0.5">
                            {lead.tags!.slice(0, 3).map((tag) => (
                              <LeadTagBadge key={tag.id} tag={tag} size="sm" />
                            ))}
                            {lead.tags!.length > 3 && (
                              <span className="px-0.5 text-xs text-muted-foreground">
                                +{lead.tags!.length - 3}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      {lead.phone && (
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {lead.phone}
                        </span>
                      )}
                    </div>
                  </Button>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  );
}
